import React, { useContext, useState, useEffect } from 'react';
import { AppContext } from '../contexts/AppContext';
import { Rol } from '../types';

const ConfiguracionScreen: React.FC = () => {
    const { user, club, supabase, fetchClubData } = useContext(AppContext);
    const [clubLogo, setClubLogo] = useState<string | null>(null);
    const [clubName, setClubName] = useState<string>('');
    const [uploading, setUploading] = useState(false);
    const [savingName, setSavingName] = useState(false);

    useEffect(() => {
        const loadClubLogo = async () => {
            if (user?.club_id && supabase) {
                const { data, error } = await supabase
                    .from('clubes')
                    .select('logo_url')
                    .eq('id', user.club_id)
                    .single();

                if (!error && data?.logo_url) {
                    setClubLogo(data.logo_url);
                }
            }
        };
        loadClubLogo();
        
        // Set initial club name
        if (club?.nombre) {
            setClubName(club.nombre);
        }
    }, [user, supabase, club]);

    if (user?.rol !== Rol.ADMIN) {
        return <p>Acceso denegado.</p>;
    }

    const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const { data, error } = await supabase.functions.invoke('upload-club-logo', {
                body: formData,
            });

            if (error) {
                console.error('Error subiendo logo:', error);
                alert('Error al subir el logo');
            } else if (data?.publicUrl) {
                setClubLogo(data.publicUrl);
                alert('Logo subido exitosamente');
                // Refresh club data to update the logo in sidebar
                if (user?.club_id) {
                    await fetchClubData(user.club_id);
                }
            }
        } catch (err) {
            console.error('Error:', err);
            alert('Error al subir el logo');
        } finally {
            setUploading(false);
        }
    };

    const handleSaveClubName = async () => {
        if (!clubName.trim() || !user?.club_id) {
            alert('Por favor ingresa un nombre válido');
            return;
        }

        setSavingName(true);
        try {
            const { error } = await supabase
                .from('clubes')
                .update({ nombre: clubName.trim() })
                .eq('id', user.club_id);

            if (error) {
                console.error('Error actualizando nombre del club:', error);
                alert('Error al actualizar el nombre del club');
            } else {
                alert('Nombre del club actualizado exitosamente');
                // Refresh club data to update everywhere
                await fetchClubData(user.club_id);
            }
        } catch (err) {
            console.error('Error:', err);
            alert('Error al actualizar el nombre del club');
        } finally {
            setSavingName(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Configuración</h2>
                <p className="text-gray-600 mt-1">Personalizá la apariencia de tu club.</p>
            </div>

            {/* Club Name Section */}
            <div className="p-4 border rounded-lg bg-gray-50 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Nombre del Club</h3>
                <div className="flex flex-col gap-4">
                    <input
                        type="text"
                        value={clubName}
                        onChange={(e) => setClubName(e.target.value)}
                        disabled={savingName}
                        className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        placeholder="Nombre de tu club"
                    />
                    <button
                        onClick={handleSaveClubName}
                        disabled={savingName}
                        className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                    >
                        {savingName ? 'Guardando...' : 'Guardar Nombre'}
                    </button>
                </div>
            </div>

            {/* Logo Section */}
            <div className="p-4 border rounded-lg bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Logo del Club</h3>
                <div className="flex flex-col items-center gap-4">
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={uploading}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                    />
                    {uploading && <p className="text-sm text-blue-600 mt-2">Subiendo logo...</p>}
                    {clubLogo && (
                        <div className="mt-4">
                            <img src={clubLogo} alt="Club Logo" className="h-16 mx-auto mb-2" />
                            <p className="text-sm text-gray-600 text-center">Vista previa del logo</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConfiguracionScreen;
